import { useState, useEffect } from "react";
import { Calendar, ArrowRight, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import { useLanguage } from "@/hooks/useLanguage";
import SEO from "@/components/SEO";
import { getPublishedPosts, BlogPost, BLOG_CATEGORIES } from "@/lib/blog-service";

const Blog = () => {
  const { t, language } = useLanguage();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const categories = ["All", ...BLOG_CATEGORIES];

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const result = await getPublishedPosts({
          language: language,
          category: selectedCategory !== "All" ? selectedCategory : undefined,
          page,
          pageSize: 9,
        });
        setPosts(result.data);
        setTotalPages(result.totalPages);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [language, selectedCategory, page]);

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setPage(1);
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Legal Blog & Resources | Daniel Albert Law Firm"
        description="Legal insights on immigration, criminal defense, family law, and more. Stay informed with our comprehensive legal guides and articles."
        keywords="legal blog, immigration news, criminal defense tips, family law advice, Houston attorney blog"
      />
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('blog.title')}</h1>
            <p className="text-xl opacity-95">
              {t('blog.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          {/* Categories Filter */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={category === selectedCategory ? "default" : "outline"}
                size="sm"
                onClick={() => handleCategoryChange(category)}
              >
                {category === "All" ? t('blog.all') : category}
              </Button>
            ))}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : posts.length === 0 ? (
            /* Empty State */
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">
                {language === 'es' ? 'No hay artículos disponibles en este momento.' : 'No articles available at this time.'}
              </p>
            </div>
          ) : (
            /* Blog Posts Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Card key={post.id} className="hover:shadow-lg transition-shadow flex flex-col">
                  {post.featured_image && (
                    <div className="aspect-video overflow-hidden rounded-t-lg">
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {post.published_at 
                          ? new Date(post.published_at).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { 
                              year: "numeric", 
                              month: "long", 
                              day: "numeric" 
                            })
                          : new Date(post.created_at).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { 
                              year: "numeric", 
                              month: "long", 
                              day: "numeric" 
                            })
                        }
                      </span>
                    </div>
                    {post.category && (
                      <div className="inline-block mb-2">
                        <span className="text-xs font-semibold bg-accent/10 text-accent px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                      </div>
                    )}
                    <CardTitle className="text-xl">{post.title}</CardTitle>
                    {post.excerpt && (
                      <CardDescription>{post.excerpt}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="mt-auto">
                    <Button asChild variant="link" className="p-0">
                      <a href={`/blog/${post.slug}`}>
                        {t('blog.read')} <ArrowRight className="ml-1 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-12">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                {language === 'es' ? 'Anterior' : 'Previous'}
              </Button>
              <span className="flex items-center px-4 text-muted-foreground">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                {language === 'es' ? 'Siguiente' : 'Next'}
              </Button>
            </div>
          )}

          {/* Newsletter Signup */}
          <div className="mt-16 max-w-2xl mx-auto bg-gradient-subtle p-8 rounded-lg shadow-md text-center">
            <h2 className="text-2xl font-bold mb-4">{t('blog.subscribe')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('blog.subscribe.desc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder={t('blog.email')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <Button variant="cta" className="whitespace-nowrap">
                {t('blog.subscribe.btn')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title="Have Legal Questions?"
        description="Our experienced attorneys are here to provide personalized guidance for your specific situation."
      />

      <Footer />
    </div>
  );
};

export default Blog;
